import React from "react";

import {
  resolvePlatformBranding,
  resolvePlatformHeroImage,
  resolvePlatformHeroKey,
} from "../utils/platformBranding";

/**
 * The canonical Platform Analytics contribution card.
 *
 * ONE card renders a platform's contribution on every surface that shows one:
 * Quantum's /cross-platform-analytics rail, the authenticated Cortex Platform
 * Analytics rail, and the public /report microsite. Before this component the
 * three drew the same idea in two different visual languages — Quantum with a
 * hero, a headline spend, an efficiency rate, a direction and a freshness
 * mark; Cortex and the public report with a stripped three-metric strip that
 * carried no rate, no direction and no freshness at all. A shared reader could
 * not tell they were looking at the same system.
 *
 * The hierarchy is fixed here and nowhere else:
 *
 *     [ platform hero ]
 *     NAME                       SHARE
 *     $SPEND
 *     RATE  LABEL                    ▲
 *     ✓ Current
 *
 * WHAT THIS COMPONENT WILL NOT DO
 *
 * It presents; it never derives. Every figure arrives already formatted by the
 * surface's own canonical formatter, so a card cannot re-round, re-scope or
 * re-attribute a number on its way to the screen. The two governed absences
 * are enforced structurally rather than by convention:
 *
 *   - `trend` renders an arrow ONLY for an explicit "up"/"down". A surface with
 *     no prior-period comparison passes null and gets no arrow — never a
 *     flat-looking one that implies a comparison was made.
 *   - `status` renders freshness ONLY for a state the surface actually
 *     established. "unknown" — the honest answer when a payload discloses no
 *     per-platform fetch stamp — renders the empty canonical status slot, so
 *     the card keeps its geometry without claiming currency it cannot prove.
 *
 * A missing rate is the caller's `—`, not a zero, and a card is never withheld
 * because one field of it is unavailable.
 */

/**
 * The canonical currency window. A platform whose last canonical fetch is
 * within it is "current"; beyond it the card says how late it is instead.
 * Matches the window Quantum's governed source layer applies when it resolves
 * per-platform connector status.
 */
export const FRESHNESS_CURRENT_MAX_MINUTES = 26 * 60;

/**
 * The direction of a prior-period movement, or null.
 *
 * Null is the point. A non-numeric delta means no comparison exists, and an
 * exactly-flat delta means the comparison found no movement — neither is an
 * improvement or a regression, and neither may be drawn as one.
 */
export const resolveTrendDirection = (deltaPts) => {
  if (typeof deltaPts !== "number" || !Number.isFinite(deltaPts) || deltaPts === 0) return null;
  return deltaPts > 0 ? "up" : "down";
};

/**
 * A platform's freshness state, from the evidence a payload actually carries.
 *
 * `isLive` is tri-state on purpose. Quantum's authenticated source layer knows
 * whether a connector is live and passes `true`/`false`; a public report is
 * never told — connector provenance is not in its projection allowlist — and
 * passes null. A null liveness is "not disclosed", not "live": the canonical
 * fetch stamp is then the only evidence, and it has to carry the claim alone.
 * A disclosed `false` can only ever downgrade, never promote.
 *
 * With no stamp at all the answer is "unknown". It is not "current" — an
 * absent stamp is an absent observation, and the card renders the canonical
 * empty status rather than a ✓ nothing supports.
 */
export const resolvePlatformFreshness = ({
  lastFetchedAt = null,
  isLive = null,
  now = Date.now(),
} = {}) => {
  const parsed = lastFetchedAt ? new Date(lastFetchedAt).getTime() : NaN;
  const minutes = Number.isFinite(parsed)
    ? Math.max(0, Math.round((now - parsed) / 60000))
    : null;
  if (isLive === false) return { state: "delayed", minutes };
  if (minutes == null) return { state: "unknown", minutes: null };
  if (minutes > FRESHNESS_CURRENT_MAX_MINUTES) return { state: "delayed", minutes };
  return { state: "current", minutes };
};

/** How long ago, in the card's compact vocabulary. */
export const formatFreshnessMinutes = (minutes) => {
  if (typeof minutes !== "number" || !Number.isFinite(minutes)) return "";
  if (minutes < 60) return `${Math.round(minutes)}m ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)}h ago`;
  return `${Math.round(minutes / (24 * 60))}d ago`;
};

/** The canonical share string: one decimal, `—` when there is no basis. */
export const formatSharePercent = (share) =>
  typeof share === "number" && Number.isFinite(share) ? `${share.toFixed(1)}%` : "—";

const classNames = (...values) => values.filter(Boolean).join(" ");

const TREND_GLYPH = { up: "▲", down: "▼" };

export default function PlatformContributionCard({
  platformKey,
  label,
  accent = null,
  share = null,
  spendText,
  efficiencyLabel = null,
  efficiencyText = null,
  trend = null,
  status = null,
  soloed = false,
  inactive = false,
  onClick = null,
  onMouseEnter = null,
  onMouseLeave = null,
  children = null,
}) {
  const branding = resolvePlatformBranding(platformKey) || {};
  const heroKey = resolvePlatformHeroKey(platformKey);
  const heroImage = resolvePlatformHeroImage(platformKey);
  const statusState = status?.state || "unknown";
  const trendDirection = trend === "up" || trend === "down" ? trend : null;

  return (
    <button
      type="button"
      className={classNames(
        "cpx-platform-card",
        soloed && "cpx-platform-card--solo",
        inactive && "cpx-platform-card--inactive"
      )}
      style={{
        "--cpx-platform-accent":
          accent || branding.color || "var(--cphere-cyan, #7ee7ff)",
      }}
      aria-pressed={soloed}
      onClick={onClick || undefined}
      onMouseEnter={onMouseEnter || undefined}
      onMouseLeave={onMouseLeave || undefined}
      data-platform-card={platformKey}
    >
      <span className="cpx-platform-card-hero" aria-hidden="true">
        <img
          className="cpx-platform-card-hero-image"
          src={heroImage}
          alt=""
          aria-hidden="true"
          data-platform-hero-key={heroKey}
          loading="lazy"
          decoding="async"
          width="640"
          height="360"
        />
      </span>
      <span className="cpx-platform-card-content">
        <span className="cpx-platform-card-head">
          <span className="cpx-platform-card-name">{label || branding.label || platformKey}</span>
          <span className="cpx-platform-card-share cpx-numeric">{formatSharePercent(share)}</span>
        </span>
        <span className="cpx-platform-card-spend cpx-numeric">{spendText}</span>
        <span className="cpx-platform-card-metrics">
          <span className="cpx-platform-card-metric">
            <span className="cpx-numeric">{efficiencyText}</span>
            <span className="cpx-platform-card-metric-label">{efficiencyLabel}</span>
          </span>
          <span
            className={classNames(
              "cpx-platform-card-trend",
              trendDirection === "up" && "cpx-trend--up",
              trendDirection === "down" && "cpx-trend--down"
            )}
          >
            {trendDirection ? TREND_GLYPH[trendDirection] : ""}
          </span>
        </span>
        <span className="cpx-platform-card-status">
          {statusState === "current" && <span className="cpx-status cpx-status--ok">✓ Current</span>}
          {statusState === "delayed" && (
            <span className="cpx-status cpx-status--warn">
              ⚠ Delayed {status?.detail || ""}
            </span>
          )}
          {statusState === "error" && (
            <span className="cpx-status cpx-status--error">✕ Connector error</span>
          )}
        </span>
        {children}
      </span>
    </button>
  );
}
